import { ComboboxProvider, SelectProvider } from '@ariakit/react'
import type { FC } from 'react'
import { useState } from 'react'
import { InputHelpText } from '@/components/ui/input-help-text'
import {
	SelectComboboxInput,
	SelectComboboxList,
	SelectComboboxPopover,
	SelectItem,
	SelectTrigger,
	SelectVirtualList
} from '@/components/ui/select-atoms-legacy'
import { Skeleton } from '@/components/ui/skeleton'
import { useGetViewOnlyGeos } from '@/features/geometry-check/queries/use-get-view-only-geos'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

const SelectContent = ({
	isLoading,
	isError,
	geos,
	fetchNextPage
}: {
	isLoading: boolean
	isError: boolean
	geos: { id: string; geoCode: string }[]
	fetchNextPage: () => void
}) => {
	const { t } = useTranslation()
	if (isLoading) {
		return (
			<div className='flex flex-col gap-1'>
				{Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
					(key) => (
						<Skeleton height={22} key={key} />
					)
				)}
			</div>
		)
	}
	if (isError) {
		return <InputHelpText error={t('errors:default')} />
	}
	if (geos.length === 0) {
		return <InputHelpText helpText={t('common:no_results')} />
	}
	return (
		<SelectVirtualList
			data={geos}
			endReached={fetchNextPage}
			itemContent={(_, geo) => (
				<SelectItem key={geo.id} value={geo.geoCode}>
					{geo.geoCode}
				</SelectItem>
			)}
			overscan={100}
			totalCount={geos.length}
		/>
	)
}

interface ViewOnlyGeoSelectProps {
	errorMessage?: string
	onChange: (value: string) => void
	value: string
}

export const ViewOnlyGeoSelect: FC<ViewOnlyGeoSelectProps> = ({
	errorMessage,
	onChange,
	value
}) => {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')

	const onSearch = debounce(setSearch, 450)
	const searchQuery = search || (value && !search ? value : undefined)
	const {
		remappedData: geos,
		isLoading,
		isError,
		fetchNextPage
	} = useGetViewOnlyGeos(searchQuery ? { geoCode: searchQuery } : undefined)

	return (
		<ComboboxProvider setValue={onSearch}>
			<SelectProvider defaultValue='' setValue={onChange} value={value}>
				<div className='flex w-full flex-col gap-1.5'>
					<SelectTrigger
						className='w-full'
						displayValue={(value) =>
							geos.find((geo) => geo.geoCode === value)?.geoCode
						}
						placeholder={t('subgeo:select_geo')}
					/>
					<InputHelpText error={errorMessage} />
				</div>
				<SelectComboboxPopover>
					<SelectComboboxInput autoFocus />
					<SelectComboboxList>
						<SelectContent
							fetchNextPage={fetchNextPage}
							geos={geos}
							isError={isError}
							isLoading={isLoading}
						/>
					</SelectComboboxList>
				</SelectComboboxPopover>
			</SelectProvider>
		</ComboboxProvider>
	)
}
